import {
  AudioFrameSource,
  ProcessorType,
  defaultStreamOptions,
} from "./audio-frame-source"
import {
  FrameProcessor,
  FrameProcessorEvent,
  FrameProcessorOptions,
  defaultFrameProcessorOptions,
  validateOptions,
} from "./frame-processor"
import { ModelName, loadModel, ort } from "./load-model"
import { log } from "./logging"
import { Message } from "./messages"
import { Model, OrtOptions, SpeechProbabilities } from "./models"

export const DEFAULT_MODEL = "legacy"

export { ort }

interface RealTimeVADCallbacks {
  /** Callback to run after each frame. The size (number of samples) of a frame is given by `frameSamples`. */
  onFrameProcessed: (
    probabilities: SpeechProbabilities,
    frame: Float32Array
  ) => Promise<void> | void

  /** Callback to run if speech start was detected but `onSpeechEnd` will not be run because the
   * audio segment is smaller than `minSpeechFrames`.
   */
  onVADMisfire: () => Promise<void> | void

  /** Callback to run when speech start is detected */
  onSpeechStart: () => Promise<void> | void

  /**
   * Callback to run when speech end is detected.
   * Takes as arg a Float32Array of audio samples between -1 and 1, sample rate 16000.
   * This will not run if the audio segment is smaller than `minSpeechFrames`.
   */
  onSpeechEnd: (audio: Float32Array) => Promise<void> | void

  /** Callback to run when speech is detected as valid. (i.e. not a misfire) */
  onSpeechRealStart: () => Promise<void> | void
}

type AssetOptions = {
  workletOptions: AudioWorkletNodeOptions
  baseAssetPath: string
  onnxWASMBasePath: string
}

type ModelOptions = {
  model: ModelName
}

export interface RealTimeVADOptions
  extends FrameProcessorOptions,
    RealTimeVADCallbacks,
    OrtOptions,
    AssetOptions,
    ModelOptions {
  audioContext?: AudioContext
  getStream: () => Promise<MediaStream>
  pauseStream: (stream: MediaStream) => Promise<void>
  resumeStream: (stream: MediaStream) => Promise<MediaStream>
  startOnLoad: boolean
  processorType: ProcessorType
}

export const getDefaultRealTimeVADOptions = (
  model: ModelName
): RealTimeVADOptions => {
  return {
    ...defaultFrameProcessorOptions,
    onFrameProcessed: () => {},
    onVADMisfire: () => {
      log.debug("VAD misfire")
    },
    onSpeechStart: () => {
      log.debug("Detected speech start")
    },
    onSpeechEnd: () => {
      log.debug("Detected speech end")
    },
    onSpeechRealStart: () => {
      log.debug("Detected real speech start")
    },
    baseAssetPath: "./",
    onnxWASMBasePath: "./",
    model: model,
    workletOptions: {},
    ...defaultStreamOptions,
    ortConfig: (ort) => {
      ort.env.logLevel = "error"
    },
    startOnLoad: true,
    processorType: "auto",
  }
}

export class MicVAD {
  private constructor(
    public options: RealTimeVADOptions,
    private readonly frameProcessor: FrameProcessor,
    private readonly model: Model,
    private readonly source: AudioFrameSource
  ) {}

  static async new(options: Partial<RealTimeVADOptions> = {}) {
    const fullOptions: RealTimeVADOptions = {
      ...getDefaultRealTimeVADOptions(options.model ?? DEFAULT_MODEL),
      ...options,
    }
    validateOptions(fullOptions)

    const { model, frameSamples, msPerFrame } = await loadModel({
      model: fullOptions.model,
      baseAssetPath: fullOptions.baseAssetPath,
      onnxWASMBasePath: fullOptions.onnxWASMBasePath,
      ortConfig: fullOptions.ortConfig,
    })

    const frameProcessor = new FrameProcessor(
      {
        positiveSpeechThreshold: fullOptions.positiveSpeechThreshold,
        negativeSpeechThreshold: fullOptions.negativeSpeechThreshold,
        redemptionMs: fullOptions.redemptionMs,
        preSpeechPadMs: fullOptions.preSpeechPadMs,
        minSpeechMs: fullOptions.minSpeechMs,
        submitUserSpeechOnPause: fullOptions.submitUserSpeechOnPause,
      },
      msPerFrame
    )

    const source = await AudioFrameSource.new({
      frameSamples,
      getStream: fullOptions.getStream,
      pauseStream: fullOptions.pauseStream,
      resumeStream: fullOptions.resumeStream,
      audioContext: fullOptions.audioContext,
      processorType: fullOptions.processorType,
      workletOptions: fullOptions.workletOptions,
      baseAssetPath: fullOptions.baseAssetPath,
    })

    const micVad = new MicVAD(fullOptions, frameProcessor, model, source)
    source.onFrame(micVad.processFrame)

    // things would be simpler if we didn't have to startOnLoad by default, but we are locked in
    if (fullOptions.startOnLoad) {
      try {
        await micVad.start()
      } catch (e) {
        console.error("Error starting micVad", e)
        throw e
      }
    }
    return micVad
  }

  get listening() {
    return this.source.listening
  }

  get errored() {
    return this.source.errored
  }

  setErrored = (error: string) => {
    this.source.setErrored(error)
  }

  start = async () => {
    this.frameProcessor.resume()
    await this.source.start()
  }

  pause = async () => {
    if (!this.source.listening) {
      return
    }
    await this.source.pause()
    this.frameProcessor.pause(this.handleFrameProcessorEvent)
    // FrameProcessor.reset used to do this. It has to happen after
    // frameProcessor.pause, because with submitUserSpeechOnPause that call
    // emits the final segment first.
    this.model.reset_state()
  }

  destroy = async () => {
    log.debug("destroy called")
    await this.source.destroy()
    await this.model.release()
  }

  setOptions = (update: Partial<FrameProcessorOptions>) => {
    this.frameProcessor.setOptions(update)
  }

  processFrame = async (frame: Float32Array) => {
    const probs = await this.model.process(frame)
    this.frameProcessor.process(frame, probs, this.handleFrameProcessorEvent)
  }

  handleFrameProcessorEvent = (ev: FrameProcessorEvent) => {
    switch (ev.msg) {
      case Message.FrameProcessed:
        void this.options.onFrameProcessed(ev.probs, ev.frame)
        break

      case Message.SpeechStart:
        void this.options.onSpeechStart()
        break

      case Message.SpeechRealStart:
        void this.options.onSpeechRealStart()
        break

      case Message.VADMisfire:
        void this.options.onVADMisfire()
        break

      case Message.SpeechEnd:
        void this.options.onSpeechEnd(ev.audio)
        break
    }
  }
}
